// 2026-09-21 - FormuLearning - Step 4 §4.4 분석(Dissolution Comparison) 구획 (이슈 #69 S6)
// 파일 위치: frontend/src/features/step4/DissolutionAnalysis.tsx
// 목적: 선택 배치를 **비교 대조약을 골라** 분석한다(M84-24, UI/UX v2.2 §4.7 §4.4).
//
// 화면 규약 넷.
//   ① 분석은 **화면 전용**이다(D4). 기준 대조약을 바꿔도 저장된 배치 f2 는 run 시점 대조약
//      기준 그대로다(BR-S4-L06). 기준이 run 시점과 다르면 그 사실을 문구로 알린다.
//   ② 배치가 바뀌면 **run 시점 대조약으로 자동 분석**하고, 대조약을 바꾼 뒤에는
//      `[Run Analysis]` 를 눌러야 갱신된다(D5 · 일지 I5) — 버튼은 기준을 바꾸는 조작에만 요구한다.
//   ③ 대조약 후보는 `COMP-REFERENCE-LOT-PICKER` 와 **같은 집합**이다(조건 불일치 Lot 은 사유와
//      함께 비활성). 단 run 시점 대조약은 지금 격자와 달라도 고를 수 있다 — 저장 f2 의 기준이다.
//   ④ 오버레이 곡선은 분석 응답의 대조약 프로파일이다 — f2 와 같은 원천을 본다.

import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  getF2Analysis, lotDisplayName, type F2Analysis, type LotBatch, type ReferenceLotCandidate,
} from '../../api/lot'
import { candidateLabel } from './ReferenceLotPicker'
import DissolutionCompareChart from './DissolutionCompareChart'

interface Props {
  projectId: string
  batch: LotBatch
  candidates: ReferenceLotCandidate[]
  /** 배치 선택기(M84-21) — 통합표와 **같은 선택 상태**를 쓴다. 부모가 그려 넘긴다. */
  batchPicker: ReactNode
}

export default function DissolutionAnalysis({ projectId, batch, candidates, batchPicker }: Props) {
  const [analysis, setAnalysis] = useState<F2Analysis | null>(null)
  // 고른 기준 대조약. `''` = 아직 모름(서버가 run 시점 대조약으로 채운다).
  const [picked, setPicked] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastBatch = useRef<string | null>(null)
  // 늦게 도착한 응답이 나중 요청의 결과를 덮지 않게 한다(배치를 빠르게 넘길 때).
  const seq = useRef(0)

  const run = async (lotId: string | null) => {
    const my = ++seq.current
    setLoading(true)
    setError(null)
    try {
      const a = await getF2Analysis(projectId, batch.id, lotId)
      if (my !== seq.current) return
      setAnalysis(a)
      setPicked(a.reference_lot_id ?? '')
    } catch {
      if (my === seq.current) setError('분석 결과를 불러오지 못했습니다.')
    } finally {
      if (my === seq.current) setLoading(false)
    }
  }

  // 배치가 바뀌면 run 시점 대조약으로 다시 시작한다. 같은 배치의 용출이 저장되어 바뀌면
  // **지금 보고 있는 기준**으로 다시 분석한다(저장 직후 판정이 낡지 않게).
  const profileSig = JSON.stringify(batch.dissolution_profiles) + String(batch.is_excluded)
  useEffect(() => {
    const batchChanged = lastBatch.current !== batch.id
    lastBatch.current = batch.id
    if (batchChanged) {
      setAnalysis(null)
      setPicked('')
      void run(null)
    } else {
      void run(analysis?.reference_lot_id ?? null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch.id, profileSig])

  const runRef = analysis?.run_reference_lot_id ?? null
  const options = candidates.map((c) => ({
    id: c.id,
    // run 시점 대조약은 지금 격자와 달라도 고를 수 있다(규약 ③)
    label: c.id === runRef ? `${candidateLabel({ ...c, selectable: true })} (run 기준)` : candidateLabel(c),
    disabled: !c.selectable && c.id !== runRef,
  }))
  // 숨김·제외된 Lot 이 run 시점 대조약이면 후보 목록에 없다 — 기준으로는 보여야 한다.
  if (analysis?.run_reference_lot_id && !options.some((o) => o.id === analysis.run_reference_lot_id)
      && analysis.reference_lot_id === analysis.run_reference_lot_id) {
    options.unshift({
      id: analysis.run_reference_lot_id,
      label: `${lotDisplayName(analysis.reference_label, analysis.reference_lot_no)} (run 기준)`,
      disabled: false,
    })
  }

  const analyzedId = analysis?.reference_lot_id ?? ''
  const offRun = analysis != null && runRef != null && analyzedId !== runRef
  const analyzedName = analysis?.reference_lot_no != null
    ? lotDisplayName(analysis.reference_label, analysis.reference_lot_no) : '—'

  return (
    <div className="mt-4" data-testid="dissolution-analysis">
      {/* §4.4 머리 — 무엇을(배치) 무엇과(대조약) 비교하는지 한 줄에 둔다(M84-21) */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
          Dissolution Comparison
        </h4>
        <span className="text-[10px] text-text-muted">분석 대상 배치</span>
        {batchPicker}
      </div>

      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-semibold uppercase text-text-secondary">Reference</span>
        <select
          value={picked}
          onChange={(e) => setPicked(e.target.value)}
          disabled={loading && analysis == null}
          data-testid="analysis-reference-select"
          className="rounded bg-bg-input px-1.5 py-0.5 text-[11px] outline-none"
          style={{ color: 'var(--color-accent-teal)', border: '1px solid var(--border-soft)' }}
        >
          {picked === '' && <option value="">—</option>}
          {options.map((o) => (
            // 조건 불일치는 `disabled` 로 남긴다 — 목록에서 빼지 않는다(BR-RND-08)
            <option key={o.id} value={o.id} disabled={o.disabled}>{o.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void run(picked || null)}
          disabled={loading || picked === '' || picked === analyzedId}
          data-testid="run-analysis"
          className="rounded border px-2.5 py-0.5 text-[10px] font-semibold text-text-body transition-opacity hover:opacity-90 disabled:opacity-40"
          style={{ borderColor: 'var(--border-purple)', backgroundColor: 'var(--bg-chip-purple)' }}
          title="고른 대조약을 기준으로 용출 곡선·용액별 f2·판정을 다시 계산합니다(저장하지 않음)"
        >
          {loading ? 'Analyzing…' : 'Run Analysis'}
        </button>
        {analysis && (
          <span className="text-[10px] text-text-muted" data-testid="analysis-reference-note">
            기준 {analyzedName}{offRun ? '' : ' (run 기준)'}
            {offRun && ' — 화면 분석값입니다. 저장된 f2는 run 기준 대조약 값입니다.'}
          </span>
        )}
      </div>

      {error && <p className="text-[10px]" style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {analysis && analysis.batch_id === batch.id && (
        <div style={{ opacity: batch.is_excluded ? 0.45 : 1 }}>
          <DissolutionCompareChart
            batchLabel={batch.batch_code}
            profiles={batch.dissolution_profiles}
            referenceProfiles={analysis.reference_profiles}
            f2BySolution={analysis.f2_by_solution}
            similarBySolution={analysis.similar_by_solution}
            f2Reason={analysis.f2_reason}
          />
          <p className="mt-1 text-[10px] text-text-muted">
            범례: Reference AVG±SD / Test AVG±SD · 0 시점 포함 · 판정 기준 f2 ≥ 50 = 유사
          </p>
        </div>
      )}
    </div>
  )
}
